import React, { useEffect, useRef, useState } from 'react';
import { injectIntl } from 'react-intl';
import { Button, Zoom } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Capacitor } from '@capacitor/core';
import BottomSheetOrDialog from '../BottomSheetOrDialog/BottomSheetOrDialog';
import { consumeIncomingShare } from '../../utils/native/incoming-share-inbox';
import {
  dismissIncomingShareImages,
  incomingShareImageSrc,
  subscribeIncomingShareImages,
  type IncomingShareImageItem,
  type IncomingSharePdfItem,
  type IncomingSharePreview,
  type IncomingShareTextItem
} from '../../utils/native/incoming-share-image';
import {
  incomingShareTooLarge,
  prepareIncomingShare,
  readIncomingShareFiles
} from '../../utils/native/incoming-share-files';
import { releaseDroppedShare } from '../../utils/native/incoming-share-drop';
import { offerIllustrationUpload } from '../../utils/native/incoming-share-handoff';
import { offerMedicalReserveFiles, type MedicalReserveFile } from '../../utils/native/incoming-share-medical';
import { history } from '../../views/App/history';
import './IncomingShareDialog.scss';

type IncomingShareDialogProps = {
  intl: {
    formatMessage: (descriptor: { id: string }) => string;
  };
  /** False until the user is signed in and on an app screen such as /home. */
  shortcutReady?: boolean;
};

/** power3.out settle in, power2.in shrink out. Same handoff as the drop box. */
const ShortcutTransition = React.forwardRef<unknown, React.ComponentProps<typeof Zoom>>(function ShortcutTransition(
  props,
  ref
) {
  return (
    <Zoom
      {...props}
      ref={ref}
      easing={{
        enter: 'cubic-bezier(0.22, 1, 0.36, 1)',
        exit: 'cubic-bezier(0.4, 0, 1, 1)'
      }}
      timeout={{ enter: 320, exit: 180 }}
    />
  );
});

const IncomingShareDialog = ({ intl, shortcutReady = false }: IncomingShareDialogProps) => {
  const [shown, setShown] = useState<IncomingSharePreview | null>(null);
  const [open, setOpen] = useState(false);
  const [failed, setFailed] = useState<Record<string, true>>({});
  const [tooLarge, setTooLarge] = useState(false);
  const openRef = useRef(false);
  const shownRef = useRef<IncomingSharePreview | null>(null);
  const readyRef = useRef(shortcutReady);
  const generation = useRef(0);
  const busy = useRef(false);
  readyRef.current = shortcutReady;

  const closeShare = (id?: string) => {
    generation.current += 1;
    busy.current = false;
    dismissIncomingShareImages();
    if (id) {
      Promise.resolve(consumeIncomingShare(id)).catch(() => {});
    }
  };

  /** Close the shortcut without deleting the native file. OCR still has to read it. */
  const hideShortcut = () => {
    busy.current = false;
    dismissIncomingShareImages();
  };

  const releaseNativeShare = (id: string) => {
    releaseDroppedShare(id);
    Promise.resolve(consumeIncomingShare(id)).catch(() => {});
  };

  const rejectOversized = (files: Array<{ file: Blob }>) => {
    if (!files.some(item => incomingShareTooLarge(item.file.size))) return false;
    busy.current = false;
    setTooLarge(true);
    return true;
  };

  const leaveForIllustration = (id: string, file: File) => {
    if (rejectOversized([{ file }])) return;
    // The file has to be waiting before the premium-financing screen mounts, or OCR never opens.
    offerIllustrationUpload({ file, password: null });
    history.push('/premium_financing');
    hideShortcut();
    releaseNativeShare(id);
  };

  const openPremiumFinancing = () => {
    if (busy.current) return;
    const share = shownRef.current;
    if (!share) return;
    const hasFile = share.items.some(item => item.kind === 'image' || item.kind === 'pdf');
    if (!hasFile) return;

    busy.current = true;
    const id = share.id;
    readIncomingShareFiles(share)
      .then(async documents => {
        if (!documents.length) {
          busy.current = false;
          return;
        }
        if (rejectOversized(documents)) return;
        const pdf = documents.find(document => document.mimeType === 'application/pdf');
        if (pdf || documents.length === 1) {
          leaveForIllustration(id, (pdf || documents[0]).file);
          return;
        }

        const items = documents.map(document => ({
          kind: 'image' as const,
          uri: URL.createObjectURL(document.file),
          name: document.name,
          mimeType: document.mimeType
        }));
        try {
          const prepared = await prepareIncomingShare({ id: share.id, items });
          leaveForIllustration(id, prepared.ocrFile);
        } finally {
          items.forEach(item => URL.revokeObjectURL(item.uri));
        }
      })
      .catch(() => {
        busy.current = false;
      });
  };

  const openMedicalReserve = () => {
    if (busy.current) return;
    const share = shownRef.current;
    if (!share) return;
    const id = share.id;
    const hasFile = share.items.some(item => item.kind === 'image' || item.kind === 'pdf');

    const leave = (files?: MedicalReserveFile[]) => {
      if (files && files.length && rejectOversized(files)) return;
      if (files && files.length) offerMedicalReserveFiles(files);
      history.push('/new/medical-reserve');
      hideShortcut();
      releaseNativeShare(id);
    };

    if (!hasFile) {
      leave();
      return;
    }

    busy.current = true;
    readIncomingShareFiles(share)
      .then(leave)
      .catch(() => {
        busy.current = false;
      });
  };

  useEffect(() => {
    return subscribeIncomingShareImages(next => {
      const previousId = shownRef.current ? shownRef.current.id : '';
      if (previousId && next && previousId !== next.id) {
        Promise.resolve(consumeIncomingShare(previousId)).catch(() => {});
      }
      shownRef.current = next;
      busy.current = false;
      generation.current += 1;
      if (next) {
        setShown(next);
        setFailed({});
        setTooLarge(false);
        if (readyRef.current) {
          openRef.current = true;
          setOpen(true);
          return;
        }
      }
      openRef.current = false;
      setOpen(false);
    });
  }, []);

  useEffect(() => {
    if (!shortcutReady) {
      openRef.current = false;
      setOpen(false);
      return;
    }
    if (shownRef.current) {
      openRef.current = true;
      setShown(shownRef.current);
      setOpen(true);
    }
  }, [shortcutReady]);

  const onClose = () => {
    const id = shownRef.current ? shownRef.current.id : undefined;
    closeShare(id);
  };

  const onExited = () => {
    if (!openRef.current && !shownRef.current) setShown(null);
  };

  const items = shown ? shown.items : [];
  const images = items.filter((item): item is IncomingShareImageItem => item.kind === 'image');
  const hasFile = items.some(item => item.kind === 'image' || item.kind === 'pdf');
  const many = images.length > 1;
  const premiumLabel = intl.formatMessage({ id: 'pfa' });

  const content = shown ? (
    <div className="incoming-share-dialog__content">
      {items
        .filter((item): item is IncomingShareTextItem => item.kind === 'text')
        .map(item => (
          <p key={item.text} className="incoming-share-dialog__text">
            {item.text}
          </p>
        ))}
      {items
        .filter((item): item is IncomingSharePdfItem => item.kind === 'pdf')
        .map(item => (
          <p key={item.uri} className="incoming-share-dialog__file">
            {item.name}
          </p>
        ))}
      {tooLarge ? (
        <p className="incoming-share-dialog__error" role="alert">
          {intl.formatMessage({ id: 'incoming-share-file-too-large' })}
        </p>
      ) : null}
      <div className="incoming-share-dialog__shortcuts">
        <Button
          fullWidth
          variant="outlined"
          color="primary"
          className="incoming-share-dialog__shortcut"
          disabled={!hasFile}
          onClick={openPremiumFinancing}
          endIcon={<ChevronRightIcon />}
        >
          {premiumLabel}
        </Button>
        <Button
          fullWidth
          variant="outlined"
          color="primary"
          className="incoming-share-dialog__shortcut"
          onClick={openMedicalReserve}
          endIcon={<ChevronRightIcon />}
        >
          {intl.formatMessage({ id: 'medical-reserve' })}
        </Button>
      </div>
      {images.map(image => {
        if (failed[image.uri]) {
          return (
            <p key={image.uri} className="incoming-share-dialog__missing">
              {intl.formatMessage({ id: 'incoming-share-image-unavailable' })}
              <span className="incoming-share-dialog__missing-name">{image.name}</span>
            </p>
          );
        }
        return (
          <img
            key={image.uri}
            className={`incoming-share-dialog__image${many ? ' incoming-share-dialog__image--stack' : ''}`}
            src={incomingShareImageSrc(image.uri, Capacitor.convertFileSrc)}
            alt={image.name}
            onError={() => setFailed(current => ({ ...current, [image.uri]: true }))}
          />
        );
      })}
    </div>
  ) : null;

  return (
    <BottomSheetOrDialog
      className="incoming-share-dialog"
      open={open}
      onClose={onClose}
      onExited={onExited}
      header={shown ? intl.formatMessage({ id: 'incoming-share-title' }) : ''}
      content={content}
      DialogProps={{
        maxWidth: 'xs',
        fullWidth: true,
        PaperProps: { className: 'incoming-share-dialog' },
        slots: { transition: ShortcutTransition }
      }}
    />
  );
};

export default injectIntl(IncomingShareDialog);
